<!-- eslint-disable max-len -->
<template lang="pug">
div.stickers-list-container(:class="{ 'shop-select-grid': selectable }")
    div.sticker-card(
      v-for="item in stickers"
      :class="{ selected: selectable && isSelected(item) }"
      @click="handleCardClick(item)"
    )
      // Sticker Image Display
      .sticker-image-wrapper
        .img-uploader.pos-r.list-image-container
          template(v-if="item.url")
            img(:src="item.url" @error="(e) => handleImageError(e)").img-fluid.list-image-item

            // Campaign Badge (if not regular sticker)
            .campaign-badge(
              v-if="item.campaignType && item.campaignType !== 'Sticker'"
              :class="getCampaignBadgeClass(item.campaignType)"
            )
              span {{ item.campaignType.toUpperCase() }}

          .select-check(
            v-if="selectable"
            :class="{ checked: isSelected(item) }"
            @click.stop="toggleSelect(item)"
          )
            svg.select-check-icon(
              v-if="isSelected(item)"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              aria-hidden="true"
            )
              path(d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z")

      // Sticker Info Display (Creator + Title)
      .sticker-info-section
        // Creator info
        .creator-info.mb-1(
          :class="{ 'is-clickable': getCreatorProfilePath(item.entity, item.organization, item.user) }"
          @click.stop="handleCreatorClick(item)"
        )
          .row.row-xs.align-items-center
            .col-auto
              img(
                :src="(item.entity==='User' && item.user && item.user.avatar) || (item.entity==='Organization' && item.organization && item.organization.avatar) ? (item.entity==='User' ? item.user.avatar : item.organization.avatar) : getPlaceholderForEntity(item)"
                @error="(e) => handleImageError(e, item)"
              ).d-block.size-24.obj-f-c.rounded-circle
            .col.lh-1
              span(v-if="item.entity==='User' && item.user").sticker-author {{ item.user.firstname }} {{ item.user.lastname }}
              span(v-else-if="item.entity==='Organization' && item.organization").sticker-author {{ item.organization.name }}
              span(v-else).sticker-author Spotlight

        // Sticker title
        .sticker-title
          h6.mb-0 {{ item.title }}

        // Sticker Pricing
        .sticker-pricing
          span.price-text
            // Show purchase price if available (already includes fees from past purchase)
            span(v-if="showPurchaseDetails && item.purchasePrice !== undefined")
              span(v-if="item.purchasePrice <= 0") Free
              span(v-else) ${{ item.purchasePrice.toFixed(2) }}
            // Ticket with price range
            span(v-else-if="item.campaignType === 'Ticket'") {{ formatTicketPriceRange(item) }}
            // Sweepstakes range
            span(v-else-if="item.campaignType === 'Sweepstakes' && item.sweepstakesSticker?.entryCost?.length > 0")
              | {{ formatPriceRangeWithFees(0, Math.max(...item.sweepstakesSticker.entryCost)) }}
            // Quick costs (name your price)
            span(v-else-if="Array.isArray(item?.quickCosts) && item.quickCosts.length > 0")
              | {{ formatPriceRangeWithFees(Math.min(...item.quickCosts), Math.max(...item.quickCosts)) }}
            // Single cost
            span(v-else-if="item.cost > 0")
              | {{ formatPriceWithFees(item.cost) }}
            span(v-else) Free

        // Shop stats (owner-only — gated by canViewStats from API)
        .shop-stats(v-if="item.socialShop?.canViewStats")
          span.shop-stat {{ formatShopViews(item.socialShop.views) }}
          span.shop-stat-separator •
          span.shop-stat {{ formatShopRevenue(item.socialShop.revenue) }}

        // Quantity stepper for selected shop stickers
        .shop-qty-row(v-if="selectable && isSelected(item) && usesTicketQuantityStepper(item)" @click.stop)
          .shop-qty-stepper
            button.shop-qty-btn(
              type="button"
              @click="updateQuantity(item, getQuantity(item) - 1)"
              :disabled="getQuantity(item) <= 1"
              aria-label="Decrease quantity"
            )
              img(src="@/assets/icons/minus.svg" alt="")
            span.shop-qty-value {{ getQuantity(item) }}
            button.shop-qty-btn.plus(
              type="button"
              @click="updateQuantity(item, getQuantity(item) + 1)"
              aria-label="Increase quantity"
            )
              img(src="@/assets/icons/plus.svg" alt="")

      // Purchase date for purchase history
      div(v-if="showPurchaseDetails && item.purchaseDate")
        .fs-12.text-muted.mt-1
          span Purchased: {{ formatDate(item.purchaseDate) }}
</template>

<script lang="ts">
import {
  defineComponent, onMounted, computed,
} from 'vue';
import gqlRequest from '../dashboard/gql-request';
import defaultProfileImage from '@/assets/images/default-profile.jpeg';
import { formatPriceWithFees, formatPriceRangeWithFees } from '@/utils/priceWithFees';
import { shouldShowShopQuantityStepper } from '@/utils/cartItemAmount';
import { getCreatorProfilePath } from '@/utils/profileNavigation';
import { isEmbeddedExperience } from '@/utils/embeddedUtils';
import { copyStickerNavQueryParams } from '@/utils/stickerShareUrl';

export default defineComponent({
  name: 'WebAppStickersGridTemplate',
  props: {
    stickers: { type: Array, default: () => [{}] },
    showPurchaseDetails: { type: Boolean, default: false },
    socialShopId: { type: String, default: null },
    selectable: { type: Boolean, default: false },
    selectedIds: { type: Array, default: () => [] },
    selectedQuantities: { type: Object, default: () => ({}) },
  },
  emits: ['toggle-select', 'update-quantity'],
  setup(props, { emit }) {
    const {
      route, router, loading,
    } = gqlRequest();

    const isEmbedded = computed(() => isEmbeddedExperience({
      path: route.path,
      query: route.query,
    }));

    function isSelected(item: any): boolean {
      return props.selectedIds.includes(item?.id);
    }

    function toggleSelect(item: any) {
      emit('toggle-select', item);
    }

    function getQuantity(item: any): number {
      const qty = props.selectedQuantities[item?.id];
      return Math.max(1, Number(qty) || 1);
    }

    function updateQuantity(item: any, quantity: number) {
      emit('update-quantity', { item, quantity: Math.max(1, quantity) });
    }

    function usesTicketQuantityStepper(item: any): boolean {
      return shouldShowShopQuantityStepper(item);
    }

    function handleCardClick(item: any) {
      gotoStickerDetails(item.id, item);
    }

    function handleCreatorClick(item: any) {
      const profilePath = getCreatorProfilePath(
        item.entity,
        item.organization,
        item.user,
      );
      if (profilePath) {
        router.push(profilePath);
      }
    }

    function gotoStickerDetails(stickerId: any, item?: any) {
      const queryParams = copyStickerNavQueryParams(route.query as Record<string, unknown>);

      // Extract organization ID from embedded organization path
      if (isEmbedded.value && route.path.includes('/embed/organization/')) {
        const orgIdMatch = route.path.match(/\/embed\/organization\/([^/?]+)/);
        if (orgIdMatch) {
          queryParams.set('embedOrganizationId', orgIdMatch[1]);
        }
      }

      // Prefer per-sticker socialShop.id, fall back to prop
      const socialShopId = item?.socialShop?.id || props.socialShopId;
      if (socialShopId) {
        queryParams.set('socialShopId', socialShopId);
      }

      const queryString = queryParams.toString();
      const queryParam = queryString ? `?${queryString}` : '';

      if (isEmbedded.value) {
        router.push(`/web/embed/sticker/${stickerId}${queryParam}`);
      } else {
        router.push(`/web/sticker/${stickerId}${queryParam}`);
      }
    }

    function formatDate(dateString: string) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }

    const getPlaceholderForEntity = (item: any): string => {
      // Check if entity is an organization
      if (item.entity === 'Organization' && item.organization) {
        return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"%3E%3Crect fill="%23e8f5f4" width="100" height="100"/%3E%3Ctext x="50" y="50" text-anchor="middle" dy=".3em" font-size="40" fill="%231fbeaf"%3E🏢%3C/text%3E%3C/svg%3E';
      }
      // Return default profile image for users
      return defaultProfileImage;
    };

    const handleImageError = (event: Event, item?: any) => {
      const target = event.target as HTMLImageElement;
      if (!target) return;

      // Prevent infinite loop if default image also fails
      if (target.src === defaultProfileImage || target.src.startsWith('data:image/svg')) return;

      // Use appropriate placeholder based on entity type
      target.src = item ? getPlaceholderForEntity(item) : defaultProfileImage;
    };

    const getCampaignBadgeClass = (campaignType: string): string => {
      if (campaignType === 'Sweepstakes') {
        return 'badge-sweepstakes';
      }
      return 'badge-default';
    };

    const formatTicketPriceRange = (item: any): string => {
      // If backend provides priceRange, try to parse it and add fees
      if (item.priceRange) {
        const rangeMatch = String(item.priceRange).match(
          /\$?(\d+(?:\.\d+)?)\s*[-–]\s*\$?(\d+(?:\.\d+)?)/,
        );
        if (rangeMatch) {
          const min = parseFloat(rangeMatch[1]);
          const max = parseFloat(rangeMatch[2]);
          return formatPriceRangeWithFees(min, max);
        }

        const singleMatch = String(item.priceRange).match(/\$?(\d+(?:\.\d+)?)/);
        if (singleMatch) {
          const price = parseFloat(singleMatch[1]);
          return formatPriceWithFees(price);
        }
      }

      // Fallback to variants if available
      if (item.varients?.length > 0) {
        const costs = item.varients.map((v: any) => Number(v.cost)).filter((c: number) => !isNaN(c));
        if (costs.length > 0) {
          const min = Math.min(...costs);
          const max = Math.max(...costs);
          return formatPriceRangeWithFees(min, max);
        }
      }

      return 'Free';
    };

    const formatShopViews = (views: number): string => {
      const count = Number(views) || 0;
      const label = count === 1 ? 'view' : 'views';
      return `${new Intl.NumberFormat('en-US').format(count)} ${label}`;
    };

    const formatShopRevenue = (revenue: number): string => {
      const amount = Number(revenue) || 0;
      const formatted = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(amount);
      return `${formatted} revenue driven`;
    };

    onMounted(async () => {
      loading.value = true;
      loading.value = false;
    });

    return {
      route,
      router,
      loading,
      isEmbedded,
      defaultProfileImage,
      // data
      gotoStickerDetails,
      handleCardClick,
      handleCreatorClick,
      getCreatorProfilePath,
      isSelected,
      toggleSelect,
      getQuantity,
      updateQuantity,
      usesTicketQuantityStepper,
      formatDate,
      getPlaceholderForEntity,
      handleImageError,
      getCampaignBadgeClass,
      formatTicketPriceRange,
      formatShopViews,
      formatShopRevenue,
      formatPriceWithFees,
      formatPriceRangeWithFees,
    };
  },
});
</script>
<style lang="scss" scoped>
.stickers-list-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  width: 100%;
  box-sizing: border-box;

  @media (min-width: 640px) {
    gap: 20px;
  }

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 24px;
  }
}

.sticker-card {
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 12px;
  overflow: hidden;

  &:hover {
    transform: translateY(-8px);

    .sticker-title h6 {
      color: #0d9488;
    }
  }
}

.sticker-image-wrapper {
  margin-bottom: 12px;
}

.list-image-container {
  position: relative;
  cursor: pointer;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  aspect-ratio: 3/4;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  background: #f9fafb;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media (min-width: 768px) {
    min-height: 240px;
    padding: 20px;
  }
}

.list-image-item {
  max-height: 100%;
  max-width: 100%;
  display: block;
  width: auto;
  height: auto;
  object-fit: contain;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  .sticker-card:hover & {
    transform: scale(1.05);
  }
}

// Campaign Badge
.campaign-badge {
  position: absolute;
  bottom: 12px;
  left: 0;
  padding: 6px 12px 6px 14px;
  border-radius: 0 8px 8px 0;
  backdrop-filter: blur(8px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

  span {
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  &.badge-sweepstakes {
    background: linear-gradient(135deg, rgba(251, 191, 36, 0.95) 0%, rgba(245, 158, 11, 0.95) 100%);
    color: #000000;
  }

  &.badge-default {
    background: linear-gradient(135deg, rgba(13, 148, 136, 0.95) 0%, rgba(20, 184, 166, 0.95) 100%);
    color: #ffffff;
  }

  @media (min-width: 768px) {
    span {
      font-size: 12px;
    }
  }
}

// Sticker Info Section
.sticker-info-section {
  padding: 0 4px;
}

.creator-info {
  margin-bottom: 8px;

  &.is-clickable {
    cursor: pointer;

    &:hover {
      .sticker-author {
        color: #0d9488;
      }
    }
  }

  .row {
    margin: 0;
  }

  .col-auto {
    padding: 0 8px 0 0;
  }

  .col {
    padding: 0;
  }

  img {
    border: 2px solid #e5e7eb;
    transition: border-color 0.2s;

    .sticker-card:hover & {
      border-color: #0d9488;
    }
  }
}

.sticker-author {
  color: #6b7280;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
  display: block;

  @media (min-width: 768px) {
    font-size: 14px;
  }
}

.sticker-title {
  margin-bottom: 6px;

  h6 {
    font-size: 15px;
    font-weight: 700;
    color: #1a1a1a;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin: 0;
    line-height: 1.3;
    transition: color 0.2s;

    @media (min-width: 768px) {
      font-size: 16px;
    }
  }
}

// Sticker Pricing
.sticker-pricing {
  padding-left: 2px;

  .price-text {
    color: #0d9488;
    font-size: 14px;
    font-weight: 700;

    @media (min-width: 768px) {
      font-size: 15px;
    }
  }
}

.shop-stats {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  padding-left: 2px;

  .shop-stat {
    color: #6b7280;
    font-size: 12px;
    font-weight: 500;
    line-height: 1.3;

    @media (min-width: 768px) {
      font-size: 13px;
    }
  }

  .shop-stat-separator {
    color: #d1d5db;
    font-size: 12px;
  }
}

.shop-select-grid {
  gap: 12px;

  @media (min-width: 640px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;
  }

  @media (min-width: 1280px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
  }

  .sticker-card {
    background: #ffffff;
    border: 2px solid #f0f0f0;
    border-radius: 12px;
    overflow: hidden;
    transition: all 0.15s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

      .sticker-title h6 {
        color: #111827 !important;
      }
    }

    &.selected {
      border-color: #2bb5a0;
      box-shadow: 0 0 0 3px rgba(43, 181, 160, 0.13);
    }
  }

  .sticker-image-wrapper {
    margin-bottom: 0;
  }

  .list-image-container {
    aspect-ratio: 1 / 1;
    min-height: 0;
    padding: 0;
    border: none;
    border-radius: 0;
    background: #f9fafb;
  }

  .campaign-badge {
    left: 10px;
    bottom: 10px;
    padding: 4px 10px;
    border-radius: 4px;
    box-shadow: none;
    backdrop-filter: none;
    background: #2bb5a0;

    span {
      font-size: 10px;
      letter-spacing: 0.06em;
      color: #ffffff;
    }

    &.badge-sweepstakes,
    &.badge-default {
      background: #2bb5a0;
      color: #ffffff;
    }
  }

  .select-check {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 2px solid #d1d5db;
    background: #ffffff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
    cursor: pointer;

    .select-check-icon {
      width: 12px;
      height: 12px;
      fill: #ffffff;
      display: block;
    }

    &.checked {
      background: #2bb5a0;
      border-color: #2bb5a0;
    }
  }

  .sticker-info-section {
    padding: 8px 10px 10px;
  }

  .creator-info {
    margin-bottom: 2px !important;

    img {
      width: 20px !important;
      height: 20px !important;
      border: none !important;
    }
  }

  .sticker-author {
    color: #9ca3af !important;
    font-size: 11px !important;
    font-weight: 400 !important;
  }

  .sticker-title {
    margin-bottom: 2px;

    h6 {
      font-size: 13px !important;
      font-weight: 700 !important;
      color: #111827 !important;
      line-height: 1.3 !important;
    }
  }

  .sticker-pricing {
    padding-left: 0;
    margin-top: 2px;

    .price-text {
      color: #2bb5a0 !important;
      font-size: 13px !important;
      font-weight: 500 !important;
      line-height: 1.3;
    }
  }

  .shop-stats {
    padding-left: 0;
    margin-top: 2px;

    .shop-stat {
      font-size: 11px !important;
    }

    .shop-stat-separator {
      font-size: 11px;
    }
  }

  .shop-qty-row {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #f3f4f6;
  }

  .shop-qty-stepper {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .shop-qty-btn {
    width: 28px;
    height: 28px;
    border-radius: 9999px;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background: #f3f4f6;
    transition: background 0.2s;
    padding: 0;

    img {
      width: 11px;
      height: 11px;
    }

    &.plus {
      background: #2bb5a0;

      img {
        filter: brightness(0) invert(1);
      }

      &:hover {
        background: rgba(43, 181, 160, 0.9);
      }
    }

    &:hover:not(:disabled) {
      background: #e5e7eb;
    }

    &.plus:hover:not(:disabled) {
      background: rgba(43, 181, 160, 0.9);
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  .shop-qty-value {
    font-size: 13px;
    font-weight: 600;
    min-width: 20px;
    text-align: center;
    color: #111827;
  }

  .list-image-item {
    width: 100%;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
}
</style>
